/*
 * Referral codes on the account — read seam (CANON V1.4 AMEND
 * REFERRALS-PER-BUYER-CODE; REFERRAL-BUILD-SPLIT-V1 §0.1 ReferralCodeV1, §3).
 *
 * Wire: my-api GET /me/referral-codes (Bearer ID token).
 *   200 {available:true,  codes:[…]}  → one card per active code
 *   200 {available:false, codes:[]}   → one quiet "couldn't load" line
 *   404 (server flag off)             → nothing
 * Every other failure degrades to null and the section renders NOTHING.
 *
 * COPY IS SETTINGS': the `copy` strings are shown exactly as sent (consumers
 * never re-derive them). `share_card` is the branded-card template (v4), kept
 * raw here and validated by lib/shareCardTemplate.
 */
import { useEffect, useState } from 'react'
import { loadSession } from '../auth/session'
import { referralsEnabled } from '../lib/referralsFlag'
import { myApiBase } from './myApi'

export interface ReferralCopy {
  my_first_line: string | null
  my_body: string | null
  share_text: string | null
  /** Settings' "used it" line (ReferralCodeV1 copy, added 2026-10-28): N=1 and
   *  every other N (incl. 0), with a {n} placeholder. Absent on an older
   *  settings ⇒ the FE's own i18n fallback. */
  uses_one: string | null
  uses_other: string | null
}

export interface ReferralShareLinks {
  whatsapp?: string
  facebook?: string
  x?: string
}

export interface ReferralCode {
  code: string
  event_id: string
  event_name: string
  event_date: string | null
  uses: number
  share_page_url: string
  share_links: ReferralShareLinks
  copy: ReferralCopy
  /** Raw settings `share_card` (v4) or null; see lib/shareCardTemplate. */
  share_card: unknown
}

export interface ReferralCodesView {
  available: boolean
  codes: ReferralCode[]
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)
const https = (v: unknown): v is string => typeof v === 'string' && /^https:\/\/\S+$/i.test(v)

function parseCode(raw: unknown): ReferralCode | null {
  if (!isObject(raw)) return null
  // Belt and braces: my-api already sends active codes only.
  if (raw.status !== 'active' || raw.enabled === false) return null
  const code = str(raw.code)
  const eventId = str(raw.event_id)
  const eventName = str(raw.event_name)
  if (!code || !eventId || !eventName || !https(raw.share_page_url)) return null
  if (typeof raw.uses !== 'number' || !Number.isSafeInteger(raw.uses) || raw.uses < 0) return null
  const copy = isObject(raw.copy) ? raw.copy : {}
  const links: ReferralShareLinks = {}
  if (isObject(raw.share_links)) {
    for (const k of ['whatsapp', 'facebook', 'x'] as const) if (https(raw.share_links[k])) links[k] = raw.share_links[k] as string
  }
  return {
    code,
    event_id: eventId,
    event_name: eventName,
    event_date: str(raw.event_date),
    uses: raw.uses,
    share_page_url: raw.share_page_url,
    share_links: links,
    copy: {
      my_first_line: str(copy.my_first_line), my_body: str(copy.my_body), share_text: str(copy.share_text),
      uses_one: str(copy.uses_one), uses_other: str(copy.uses_other),
    },
    share_card: isObject(raw.share_card) ? raw.share_card : null,
  }
}

/** DEFENSIVE parse of a GET /me/referral-codes body. A malformed envelope ⇒
 *  null; a malformed or non-active code is dropped. */
export function parseReferralCodes(raw: unknown): ReferralCodesView | null {
  if (!isObject(raw)) return null
  if (typeof raw.available !== 'boolean' || !Array.isArray(raw.codes)) return null
  const codes = (raw.codes as unknown[]).map(parseCode).filter((c): c is ReferralCode => c !== null)
  return { available: raw.available, codes }
}

/** GET /me/referral-codes — NEVER throws. 404 and every failure ⇒ null. */
export async function fetchMyReferralCodes(
  idToken: string,
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ReferralCodesView | null> {
  try {
    const res = await fetchImpl(`${base.replace(/\/$/, '')}/me/referral-codes`, {
      headers: { Authorization: `Bearer ${idToken}` },
    })
    if (res.status !== 200) return null
    return parseReferralCodes(await res.json())
  } catch {
    return null
  }
}

export interface ReferralCodesState {
  /** false until the read settles (or when the flag is off / no session). */
  loaded: boolean
  /** null: flag off, 404, or any failed read (render nothing). */
  view: ReferralCodesView | null
}

/** Read the caller's referral codes once per mount. Flag off ⇒ no request. */
export function useReferralCodes(env: Record<string, string | undefined> = import.meta.env): ReferralCodesState {
  const enabled = referralsEnabled(env)
  const base = myApiBase(env)
  const [state, setState] = useState<ReferralCodesState>({ loaded: false, view: null })
  useEffect(() => {
    if (!enabled || !base) return
    const session = loadSession()
    if (!session) return
    let cancelled = false
    void fetchMyReferralCodes(session.idToken, base).then((v) => {
      if (!cancelled) setState({ loaded: true, view: v })
    })
    return () => { cancelled = true }
  }, [enabled, base])
  return enabled ? state : { loaded: true, view: null }
}
